"use client";

import type { ReactNode } from "react";
import { useLocale, useTranslations } from "use-intl";

import { GlobeIcon, SparkleIcon } from "@/components/appIcons";
import type { DealOffer } from "@/components/marketplace/deal-offers-types";
import {
  isInternationalDealOffer,
  offerMarketDisplay,
  offerMovementSignal,
} from "@/components/marketplace/deal-offers-utils";
import { topPriceIndicator } from "@/components/marketplace/price-indicator-chips";
import { useOfferMediaPacking } from "@/components/marketplace/useOfferMediaPacking";
import { cn } from "@/lib/utils";

type PriceMovement = ReturnType<typeof offerMovementSignal>;

function visibleMovement(movement: PriceMovement, hasIndicator: boolean) {
  if (movement?.kind === "flat" && hasIndicator) return null;
  return movement;
}

interface SignalItem {
  key: string;
  content: ReactNode;
  className: string;
  label?: string;
}

function movementItem(movement: PriceMovement): SignalItem | null {
  if (!movement) return null;
  const MovementIcon = movement.meta.icon;
  const label =
    movement.kind === "flat" ? movement.meta.label : `${movement.meta.label} ${movement.percent}`;
  return {
    key: "movement",
    label,
    className: cn("font-bold", movement.meta.className),
    content: (
      <>
        <MovementIcon aria-hidden="true" className="text-sm" />
        {movement.kind === "flat" ? movement.meta.label : movement.percent}
      </>
    ),
  };
}

function factItem(key: string, content: string | null): SignalItem | null {
  if (!content) return null;
  return { key, className: "text-muted-foreground", content };
}

/** The market names the origin; the globe (plus a hidden "International") flags a foreign offer. */
function marketItem(
  offer: DealOffer,
  market: string | null,
  internationalLabel: string,
): SignalItem | null {
  if (!isInternationalDealOffer(offer)) return factItem("market", market);
  return {
    key: "market",
    className: "text-muted-foreground",
    label: market ? `${internationalLabel} · ${market}` : internationalLabel,
    content: (
      <>
        <GlobeIcon aria-hidden="true" className="text-sm" />
        {market ? (
          <>
            <span className="sr-only">{internationalLabel} · </span>
            {market}
          </>
        ) : (
          internationalLabel
        )}
      </>
    ),
  };
}

function compactSignalItems(items: SignalItem[], offer: DealOffer, internationalLabel: string) {
  const hasForeignOrigin = Boolean(
    offer.shippingOriginCountry &&
    /^[A-Z]{2}$/.test(offer.shippingOriginCountry) &&
    /^[A-Z]{2}$/.test(offer.country) &&
    offer.shippingOriginCountry !== offer.country,
  );
  const international = isInternationalDealOffer(offer) || hasForeignOrigin;
  const signals = items.filter(
    (item) => !["condition", "availability", "market"].includes(item.key),
  );
  return international ? [...signals, factItem("market", internationalLabel)!] : signals;
}

function useSignalItems(offer: DealOffer, compact: boolean): SignalItem[] {
  const locale = useLocale();
  const t = useTranslations("shared.affiliates");
  const packing = useOfferMediaPacking(offer.releaseMediaFormatSummary, offer.formatCategory);
  const indicator = topPriceIndicator(offer.priceIndicators);
  const movement = visibleMovement(
    offerMovementSignal(offer, locale, t, { allowLastChangeFallback: false }),
    indicator !== null,
  );
  const IndicatorIcon = indicator?.icon;
  const items: Array<SignalItem | null> = [
    movementItem(movement),
    indicator && IndicatorIcon
      ? {
          key: "indicator",
          className: cn("font-bold", indicator.textClassName),
          content: (
            <>
              <IndicatorIcon aria-hidden="true" className="text-sm" />
              {t(indicator.labelKey)}
            </>
          ),
        }
      : null,
    offer.tags.includes("super_offer")
      ? {
          key: "super",
          className: "font-bold text-amber-300",
          content: (
            <>
              <SparkleIcon aria-hidden="true" className="text-sm" />
              {t("superOffer")}
            </>
          ),
        }
      : null,
    isInternationalDealOffer(offer)
      ? marketItem(offer, offerMarketDisplay(offer, locale), t("internationalOffer"))
      : null,
    factItem("packing", packing),
  ];
  const visibleItems = items.filter((item): item is SignalItem => item !== null);
  return compact ? compactSignalItems(visibleItems, offer, t("internationalOffer")) : visibleItems;
}

/**
 * Signals under the hero price as one line of text (not chips): the price movement, the single
 * highest-priority price indicator, then offer facts (super offer, origin, condition,
 * availability). Colored text keeps the meaning without competing with the price.
 * Each separator dot sits in the gap before its item; the row clips its left edge so a wrapped
 * line never starts with a dot.
 */
export function DealOfferPriceSignalsRow({
  offer,
  compact = false,
}: {
  offer: DealOffer;
  compact?: boolean;
}) {
  const items = useSignalItems(offer, compact);

  if (items.length === 0) return null;

  return (
    <p className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 overflow-hidden text-sm leading-5">
      {items.map((item) => (
        <span
          key={item.key}
          className={cn(
            "relative inline-flex items-center gap-1 whitespace-nowrap",
            "before:absolute before:-left-1.5 before:-translate-x-1/2 before:text-muted-foreground/60 before:content-['·']",
            item.className,
          )}
          title={item.label}
        >
          {item.content}
        </span>
      ))}
    </p>
  );
}
